import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useCompany, useServices } from '../content/ContentProvider';

/**
 * The business's structured data, as JSON-LD.
 *
 * Ported from the PHP theme's inc/schema.php and built from the same live
 * company payload, so the two front ends describe the same business and a
 * change to the hours or phone number reaches both.
 *
 * Two deliberate choices, both inherited:
 *
 * - No street address. This is a service-area business; the owner does not
 *   publish one, and the markup says where we WORK (areaServed) rather than
 *   where we are. City and province only, exactly like the PHP theme.
 * - Only the home page and the contact page carry the block, because those
 *   are the pages that describe the business itself.
 *
 * Injected at runtime rather than baked into index.html so it can never drift
 * from the REST data. Google's guidance reserves server-rendered JSON-LD for
 * time-sensitive markup (offers, prices); a contractor's profile is not that.
 */
export default function SchemaOrg() {
	const company = useCompany();
	const services = useServices();
	const { pathname } = useLocation();

	const carryingPage = pathname === '/' || pathname === '/contact';

	useEffect(() => {
		if (!carryingPage || !company.name) {
			return undefined;
		}

		const origin = window.location.origin;

		const schema = {
			'@context': 'https://schema.org',
			'@type': 'GeneralContractor',
			'@id': `${origin}/#business`,
			name: company.name,
			legalName: company.legal_name,
			slogan: company.slogan,
			description:
				'Top Tile Construction is a tile and interior finishing contractor serving Edmonton, Alberta. We install tile, flooring, drywall, baseboards, trim, and doors, and we handle interior and exterior painting.',
			url: `${origin}/`,
			telephone: '+17806917454',
			email: company.email,
			founder: { '@type': 'Person', name: company.owner },
			address: {
				'@type': 'PostalAddress',
				addressLocality: company.city,
				addressRegion: company.region_code,
				addressCountry: company.country,
			},
			areaServed: [company.city, ...(company.nearby ?? [])].map((place) => ({
				'@type': 'City',
				name: place,
			})),
			priceRange: '$$',
			currenciesAccepted: 'CAD',
			// Ties the site to the Google Business Profile, which is where the
			// reviews live.
			sameAs: [company.google_profile_url].filter(Boolean),
			hasOfferCatalog: {
				'@type': 'OfferCatalog',
				name: 'Tile and interior finishing services',
				itemListElement: services.map((service) => ({
					'@type': 'Offer',
					itemOffered: {
						'@type': 'Service',
						name: service.title,
						description: service.summary,
					},
				})),
			},
			openingHoursSpecification: (company.hours ?? [])
				.filter((block) => block.days?.length && block.opens)
				.map((block) => ({
					'@type': 'OpeningHoursSpecification',
					dayOfWeek: block.days,
					opens: block.opens,
					closes: block.closes,
				})),
			logo: `${origin}/wp-content/themes/top-tile-construction/assets/img/logos/primary-logo.svg`,
		};

		schema.image = schema.logo;

		const script = document.createElement('script');
		script.type = 'application/ld+json';
		script.id = 'ttc-schema';
		script.textContent = JSON.stringify(schema);
		document.head.appendChild(script);

		return () => script.remove();
	}, [carryingPage, company, services]);

	return null;
}
